import { useEffect, useRef, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { cn } from '@/utils/cn';
import { useRegion } from '@/utils/useRegion';

const SECTION_LINKS = [
  { labelKey: 'clearFamily', to: '#clear-family' },
  { labelKey: 'colorFamily', to: '#color-family' },
  { labelKey: 'standardFamily', to: '#standard-family' },
  { labelKey: 'lensCare', to: '#lens-care' },
] as const;

function scrollToHash(e: MouseEvent<HTMLElement>, hash: string) {
  const id = hash.replace(/^#/, '');
  const target = document.getElementById(id);
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth' });
  window.history.pushState(null, '', hash);
}

interface ProductsSecondaryNavProps {
  /** When true, the nav slides into view + fades to full opacity. */
  visible: boolean;
}

/**
 * Secondary nav for the Products page — pinned 8px below the main NavigationPill.
 *
 * Styling: identical pill chrome to the main nav (oat/70 + backdrop-blur,
 * divider-light/40 full border, shadow-pill, rounded-lg).
 *
 * Width / alignment:
 *   - Mobile: pill hugs its content and right-aligns to the same right edge as
 *     the main NavigationPill (shared `max-w-[1400px]` + `px-6` outer wrapper).
 *     Buttons stack vertically and right-align to the pill's right edge.
 *   - Tablet (md): fills the inner content row and centers buttons horizontally.
 *   - lg+: pill hugs content and the row centers it.
 *
 * Buttons: 4 section anchors styled like the main nav page links
 * (`Button transparent/large/black` — animated underline-on-hover).
 *
 * Visibility:
 *   - Controlled by the `visible` prop. On the Products page, the parent
 *     toggles this based on hero-out-of-view (md+) or active scrolling (mobile).
 *   - Hidden state: `opacity-0 -translate-y-2 pointer-events-none`.
 *   - Visible state: `opacity-100 translate-y-0 pointer-events-auto`.
 *   - Transition: `duration-fast ease-in` (snappy 120ms).
 */
export function ProductsSecondaryNav({ visible }: ProductsSecondaryNavProps) {
  const { t } = useTranslation();
  // Keep the hidden nav (and its section links) out of the tab order and a11y
  // tree. Set via the DOM `inert` property — React 18 drops the attribute.
  const navWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (navWrapperRef.current) navWrapperRef.current.inert = !visible;
  }, [visible]);

  const country = useRegion();
  const visibleLinks = SECTION_LINKS.filter(
    (link) => !(link.labelKey === 'lensCare' && country === 'US'),
  );
  // `top` lands this nav exactly 8px below the main pill's bottom edge.
  // The pill is `top-6` (24px) + p-4 (32px) + content row. At md+ the
  // tallest row item is the language-dropdown trigger (~34px), so pill
  // height = 66px and pill bottom = 90px → 8px gap puts this nav at 98px.
  // On mobile only the hamburger + logo are visible, so the row is ~24px
  // and pill bottom = 80px → 88px.
  return (
    <div className="pointer-events-none fixed inset-x-0 top-[88px] z-30 flex justify-center px-6 md:top-[98px]">
      <div
        ref={navWrapperRef}
        aria-hidden={!visible}
        className={cn(
          'flex w-full max-w-[1400px] transition-all duration-fast ease-in',
          'justify-end md:justify-center',
          visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
        )}
      >
        <nav
          aria-label={t('products.secondaryNav.label')}
          className={cn(
            'w-auto md:w-full lg:w-auto',
            'rounded-lg',
            'bg-[#edeceb]/70 shadow-pill backdrop-blur-pill',
            'px-8 py-4',
            visible && 'pointer-events-auto',
          )}
        >
          <ul className="flex flex-col items-end gap-3 md:flex-row md:items-center md:justify-center md:gap-6 lg:gap-14">
            {visibleLinks.map((link) => (
              <li key={link.to}>
                <Button
                  href={link.to}
                  onClick={(e) => scrollToHash(e, link.to)}
                  variant="transparent"
                  size="large"
                  tone="black"
                >
                  {t(`products.secondaryNav.${link.labelKey}`)}
                </Button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}
